/* ─────────────────────────────────────────────────────────────────────────────
 * eoh-chat-visuals.js — inline visualizations for the EOH chat
 *
 * The assistant emits fenced spec blocks; formatMarkdown() (eoh-chat.js) turns them
 * into placeholder divs; this engine renders them into live visuals:
 *   ```chart    → Chart.js (bar/line/area/pie/doughnut/radar/polarArea)
 *   ```stat     → KPI / stat cards
 *   ```mermaid  → Mermaid flow / relationship / sequence diagrams
 * Plus a light "modern polish" CSS layer for normal/config answers (tables, badges,
 * callouts, step cards).
 *
 * Self-contained: lazy-loads Chart.js + Mermaid from CDN (override via
 * window._chartSrc / window._mermaidSrc to serve from Shopify assets), watches #msgs
 * with a debounced MutationObserver, and renders any un-rendered placeholders. No
 * scripts are injected through innerHTML, so it stays DOMPurify-safe.
 * ───────────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";
  if (window._eohVisualsLoaded) return;
  window._eohVisualsLoaded = true;

  var CHART_SRC   = window._chartSrc   || 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
  var MERMAID_SRC = window._mermaidSrc || 'https://cdnjs.cloudflare.com/ajax/libs/mermaid/10.9.3/mermaid.min.js';

  // Brand-led palette (green primary). Accent hues read on light + dark + warm themes.
  var PALETTE = ['#0a8a5f', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b'];
  // Read the ACTIVE theme's colours from the app CSS vars so visuals adapt to
  // dark / light / warm rather than being hardcoded.
  function _cssVar(name, fb) { try { var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim(); return v || fb; } catch (_) { return fb; } }
  function _theme() { return { ink: _cssVar('--text', '#e8edf5'), muted: _cssVar('--muted', '#7d8ba3'), grid: _cssVar('--border', 'rgba(148,163,184,.18)') }; }
  function _isLightTheme() { return /(^|\s)(light|warm)(\s|$)/.test(document.documentElement.className || ''); }

  function _alpha(hex, a) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    if (!m) return hex;
    return 'rgba(' + parseInt(m[1], 16) + ',' + parseInt(m[2], 16) + ',' + parseInt(m[3], 16) + ',' + a + ')';
  }

  // ── Lazy loaders ─────────────────────────────────────────────────────────────
  function loadScript(src) {
    return new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = res; s.onerror = function () { rej(new Error('load failed: ' + src)); };
      document.head.appendChild(s);
    });
  }
  var _chartP = null, _mermaidP = null;
  function ensureChart() {
    if (window.Chart) return Promise.resolve();
    if (!_chartP) _chartP = loadScript(CHART_SRC).then(function () {
      try { window.Chart.defaults.font.family = '-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif'; } catch (_) {}
    });
    return _chartP;
  }
  function ensureMermaid() {
    if (window.mermaid) return Promise.resolve();
    if (!_mermaidP) _mermaidP = loadScript(MERMAID_SRC).then(function () {
      try {
        window.mermaid.initialize({
          startOnLoad: false, securityLevel: 'strict', theme: 'dark',
          themeVariables: { primaryColor: '#0f2a20', primaryBorderColor: '#0a8a5f', primaryTextColor: '#e6efe9', lineColor: '#5b6b63', fontSize: '14px' }
        });
      } catch (_) {}
    });
    return _mermaidP;
  }

  // ── Chart rendering ────────────────────────────────────────────────────────
  function _chartOptions(type, custom) {
    var th = _theme();
    var isCircular = /^(pie|doughnut|polararea)$/.test(type);
    var o = {
      responsive: true, maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: 'bottom', labels: { color: th.ink, usePointStyle: true, boxWidth: 8, padding: 14 } },
        tooltip: { backgroundColor: 'rgba(15,23,42,.95)', borderColor: 'rgba(148,163,184,.25)', borderWidth: 1, padding: 10, titleColor: '#fff', bodyColor: '#e2e8f0' }
      },
      scales: isCircular ? {} : {
        x: { grid: { color: th.grid }, ticks: { color: th.muted } },
        y: { grid: { color: th.grid }, ticks: { color: th.muted }, beginAtZero: true }
      }
    };
    if (custom && typeof custom === 'object') { try { return _deepMerge(o, custom); } catch (_) {} }
    return o;
  }
  function _deepMerge(a, b) {
    Object.keys(b || {}).forEach(function (k) {
      if (b[k] && typeof b[k] === 'object' && !Array.isArray(b[k])) { a[k] = _deepMerge(a[k] || {}, b[k]); }
      else a[k] = b[k];
    });
    return a;
  }

  function renderChart(el) {
    var raw = el.getAttribute('data-eoh-spec'); if (!raw) { el.setAttribute('data-rendered', '1'); return; }
    var spec; try { spec = JSON.parse(decodeURIComponent(raw)); } catch (e) { _fail(el, 'chart'); return; }
    ensureChart().then(function () {
      el.innerHTML = '';
      if (spec.title) { var t = document.createElement('div'); t.className = 'eoh-viz-title'; t.textContent = spec.title; el.appendChild(t); }
      var wrap = document.createElement('div'); wrap.className = 'eoh-viz-canvas'; var cv = document.createElement('canvas');
      wrap.appendChild(cv); el.appendChild(wrap);

      var type = String(spec.type || 'bar').toLowerCase();
      var fill = false; if (type === 'area') { type = 'line'; fill = true; }
      var circular = /^(pie|doughnut|polararea)$/.test(type);
      var chartType = type === 'polararea' ? 'polarArea' : type;

      var datasets = (spec.datasets || []).map(function (ds, i) {
        var c = PALETTE[i % PALETTE.length];
        var d = ds.data || [];
        var base = {
          label: ds.label || ('Series ' + (i + 1)),
          data: d,
          borderColor: circular ? '#0f172a' : c,
          backgroundColor: circular ? d.map(function (_, j) { return PALETTE[j % PALETTE.length]; })
                          : (type === 'line' ? _alpha(c, fill || ds.fill ? 0.18 : 0.9) : _alpha(c, 0.78)),
          borderWidth: circular ? 1.5 : 2,
          fill: !!(fill || ds.fill), tension: 0.35, pointRadius: 3, pointHoverRadius: 5
        };
        return _deepMerge(base, ds.options || {});
      });

      try { if (window.Chart.getChart) { var ex = window.Chart.getChart(cv); if (ex) ex.destroy(); } } catch (_) {}
      try {
        new window.Chart(cv, { type: chartType, data: { labels: spec.labels || [], datasets: datasets }, options: _chartOptions(type, spec.options) });
      } catch (e) { _fail(el, 'chart'); return; }
      el.setAttribute('data-rendered', '1');
    }).catch(function () { _fail(el, 'chart'); });
  }

  // ── Stat / KPI cards ─────────────────────────────────────────────────────────
  function renderStat(el) {
    var raw = el.getAttribute('data-eoh-spec'); if (!raw) { el.setAttribute('data-rendered', '1'); return; }
    var spec; try { spec = JSON.parse(decodeURIComponent(raw)); } catch (e) { _fail(el, 'stat'); return; }
    var items = spec.items || (Array.isArray(spec) ? spec : []);
    var html = '';
    if (spec.title) html += '<div class="eoh-viz-title">' + _esc(spec.title) + '</div>';
    html += '<div class="eoh-stat-grid">';
    items.forEach(function (it) {
      var trend = String(it.trend || '').toLowerCase();
      var dcls = trend === 'up' ? 'up' : (trend === 'down' ? 'down' : 'flat');
      var arrow = trend === 'up' ? '▲' : (trend === 'down' ? '▼' : '');
      html += '<div class="eoh-stat-card">'
        + '<div class="eoh-stat-label">' + _esc(it.label || '') + '</div>'
        + '<div class="eoh-stat-value">' + _esc(String(it.value == null ? '' : it.value)) + '</div>'
        + (it.delta ? '<div class="eoh-stat-delta ' + dcls + '">' + arrow + ' ' + _esc(String(it.delta)) + '</div>' : '')
        + '</div>';
    });
    html += '</div>';
    el.innerHTML = html;
    el.setAttribute('data-rendered', '1');
  }

  // ── Mermaid diagrams ─────────────────────────────────────────────────────────
  function renderMermaid(el) {
    var raw = el.getAttribute('data-eoh-spec'); if (!raw) { el.setAttribute('data-rendered', '1'); return; }
    var code = ''; try { code = decodeURIComponent(raw); } catch (e) { code = raw; }
    ensureMermaid().then(function () {
      try { window.mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: _isLightTheme() ? 'neutral' : 'dark' }); } catch (_) {}
      var id = 'mmd' + Math.random().toString(36).slice(2, 9);
      try {
        var out = window.mermaid.render(id, code);
        if (out && typeof out.then === 'function') {
          out.then(function (r) { el.innerHTML = '<div class="eoh-mermaid-inner">' + r.svg + '</div>'; el.setAttribute('data-rendered', '1'); })
             .catch(function () { _fail(el, 'mermaid'); });
        } else if (out && out.svg) {
          el.innerHTML = '<div class="eoh-mermaid-inner">' + out.svg + '</div>'; el.setAttribute('data-rendered', '1');
        } else { _fail(el, 'mermaid'); }
      } catch (e) { _fail(el, 'mermaid'); }
    }).catch(function () { _fail(el, 'mermaid'); });
  }

  function _fail(el, kind) {
    el.innerHTML = '<div class="eoh-viz-err">Couldn\'t render this ' + kind + '.</div>';
    el.setAttribute('data-rendered', '1');
  }

  // ── Free-form AI visual — sanitized HTML/SVG, never any script/handler ────────
  var _dpP = null;
  function ensureDP() {
    if (window.DOMPurify) return Promise.resolve();
    if (!_dpP) _dpP = loadScript(window._dpSrc || 'https://cdnjs.cloudflare.com/ajax/libs/dompurify/3.1.6/purify.min.js');
    return _dpP;
  }
  var VIS_CFG = {
    USE_PROFILES: { html: true, svg: true },       // presentational HTML + safe SVG
    ADD_ATTR: ['style'],                            // inline styles for layout (values are sanitized)
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea',
                  'button', 'link', 'base', 'meta', 'audio', 'video', 'source'],
    FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onmouseenter', 'onmouseleave',
                  'onfocus', 'onanimationstart', 'onanimationend', 'onbegin', 'onend', 'srcset', 'src'],
    ALLOW_DATA_ATTR: false
  };
  function renderVisual(el) {
    var raw = el.getAttribute('data-eoh-spec'); if (!raw) { el.setAttribute('data-rendered', '1'); return; }
    var html = ''; try { html = decodeURIComponent(raw); } catch (e) { html = raw; }
    ensureDP().then(function () {
      if (!window.DOMPurify) { el.setAttribute('data-rendered', '1'); return; } // never inject unsanitized
      try { el.innerHTML = window.DOMPurify.sanitize(html, VIS_CFG); }
      catch (e) { _fail(el, 'visual'); return; }
      el.setAttribute('data-rendered', '1');
    }).catch(function () { el.setAttribute('data-rendered', '1'); }); // sanitizer unavailable → render nothing
  }

  // Auto-colour blockquotes by leading keyword (Note/Tip/Warning/Prerequisite).
  function polishBlockquotes() {
    try {
      document.querySelectorAll('#msgs .mbubble blockquote:not([data-eoh-cq])').forEach(function (bq) {
        bq.setAttribute('data-eoh-cq', '1');
        var t = (bq.textContent || '').trim().toLowerCase();
        var cls = /^(note|important|key)\b/.test(t) ? 'note'
                : /^tip\b/.test(t) ? 'tip'
                : /^(warning|caution|watch out)\b/.test(t) ? 'warn'
                : /^(prerequisite|prereq|before you)\b/.test(t) ? 'prereq' : '';
        if (cls) bq.classList.add('eoh-cq-' + cls);
      });
    } catch (_) {}
  }
  function _esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); }

  // ── Scan + observe ────────────────────────────────────────────────────────────
  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.eoh-chart:not([data-rendered])').forEach(renderChart);
    root.querySelectorAll('.eoh-stat:not([data-rendered])').forEach(renderStat);
    root.querySelectorAll('.eoh-mermaid:not([data-rendered])').forEach(renderMermaid);
    root.querySelectorAll('.eoh-visual:not([data-rendered])').forEach(renderVisual);
    polishBlockquotes();
  }

  var _debounce = null;
  function schedule() { clearTimeout(_debounce); _debounce = setTimeout(function () { scan(document.getElementById('msgs') || document.body); }, 180); }

  function start() {
    var msgs = document.getElementById('msgs');
    injectCSS();
    scan(msgs || document.body);
    if (msgs && window.MutationObserver) {
      new MutationObserver(schedule).observe(msgs, { childList: true, subtree: true });
    } else {
      // Fallback: poll a few times if #msgs isn't ready yet.
      var n = 0; var iv = setInterval(function () { if (document.getElementById('msgs')) { clearInterval(iv); start(); } else if (++n > 40) clearInterval(iv); }, 250);
    }
  }
  window.EOHVisuals = { scan: function () { scan(document.getElementById('msgs') || document.body); } };

  // ── Styles: visuals + subtle "modern" polish for config/step answers ──────────
  function injectCSS() {
    if (document.getElementById('eoh-visuals-css')) return;
    var css = document.createElement('style'); css.id = 'eoh-visuals-css';
    css.textContent = [
      /* containers — surfaces & borders follow the active theme */
      '.eoh-chart,.eoh-stat,.eoh-mermaid{margin:14px 0;padding:14px 16px;background:var(--bg3,rgba(148,163,184,.06));border:1px solid var(--border,rgba(148,163,184,.16));border-radius:12px;}',
      '.eoh-viz-title{font-size:13px;font-weight:700;letter-spacing:.02em;color:var(--text,#e8edf5);margin-bottom:10px;}',
      '.eoh-viz-canvas{position:relative;height:320px;}',
      '.eoh-viz-err{font-size:12px;color:#e0806a;}',
      /* stat cards */
      '.eoh-stat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px;}',
      '.eoh-stat-card{padding:12px 14px;border:1px solid var(--border,rgba(148,163,184,.16));border-radius:10px;background:var(--bg2,rgba(148,163,184,.05));}',
      '.eoh-stat-label{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:var(--muted,#7d8ba3);}',
      '.eoh-stat-value{font-size:22px;font-weight:800;color:var(--text,#f1f5f4);margin-top:3px;line-height:1.1;}',
      '.eoh-stat-delta{font-size:12px;font-weight:600;margin-top:4px;}',
      '.eoh-stat-delta.up{color:#22a06b;}.eoh-stat-delta.down{color:#e5484d;}.eoh-stat-delta.flat{color:var(--muted,#7d8ba3);}',
      '.eoh-mermaid-inner{overflow:auto;}.eoh-mermaid svg{max-width:100%;height:auto;}',
      '.eoh-visual{margin:14px 0;}.eoh-visual svg{max-width:100%;height:auto;}',
      /* auto-coloured callouts (blockquotes by keyword) — accent hue + theme text */
      '#msgs .mbubble blockquote.eoh-cq-note{border-left-color:#3b82f6;background:rgba(59,130,246,.1);}',
      '#msgs .mbubble blockquote.eoh-cq-tip{border-left-color:#0a8a5f;background:rgba(10,138,95,.11);}',
      '#msgs .mbubble blockquote.eoh-cq-warn{border-left-color:#f59e0b;background:rgba(245,158,11,.1);}',
      '#msgs .mbubble blockquote.eoh-cq-prereq{border-left-color:#8b5cf6;background:rgba(139,92,246,.1);}',
      /* ── subtle polish on answer content (theme-aware) ── */
      '#msgs .mbubble table{border-collapse:collapse;width:100%;margin:12px 0;font-size:13.5px;border-radius:10px;overflow:hidden;}',
      '#msgs .mbubble thead th{background:rgba(10,138,95,.16);color:var(--text,#e8edf5);text-align:left;font-weight:700;}',
      '#msgs .mbubble th,#msgs .mbubble td{border:1px solid var(--border,rgba(148,163,184,.16));padding:8px 11px;color:var(--text,#e8edf5);}',
      '#msgs .mbubble tbody tr:nth-child(even){background:rgba(128,128,128,.06);}',
      '#msgs .mbubble :not(pre)>code{background:rgba(10,138,95,.14);color:var(--text,#e8edf5);border:1px solid rgba(10,138,95,.28);padding:1px 6px;border-radius:5px;font-size:.9em;}',
      '#msgs .mbubble blockquote{margin:12px 0;padding:10px 14px;border-left:3px solid #0a8a5f;background:rgba(10,138,95,.08);border-radius:0 8px 8px 0;color:var(--text,#e8edf5);}',
      '#msgs .mbubble h2,#msgs .mbubble h3{padding-bottom:5px;border-bottom:1px solid var(--border,rgba(148,163,184,.16));}'
    ].join('');
    document.head.appendChild(css);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
